import React, { Component } from 'react';
import styled from 'styled-components';
import { Button } from '@zocdoc-frontend-common/components-button';
import { DateOfBirthPicker } from '@zocdoc-frontend-common/components-dateofbirthpicker';
import ErrorMessage from '@zocdoc-frontend-common/components-errormessage';
import { FirstLastName } from '@zocdoc-frontend-common/components-firstlastname';
import { Radio, RadioGroup } from '@zocdoc-frontend-common/components-radio';
import Textbox from '@zocdoc-frontend-common/components-textbox';
import { Row } from '@zocdoc-frontend-common/styles-grid';
import { FormRow } from '@zocdoc-frontend-common/styles-grid';
import { FontFamily } from '@zocdoc-frontend-common/styles-typography';
import { NEW_SUBPATIENT_STRINGS } from './constants';

/**
 * @param {boolean} emailFormatError
 * @param {string} invalidEmailMessage
 * @param {function(date: string): void} onBirthDateChange
 * @param {function(): void} onClickContinue
 * @param {function(): void} onDayFocus
 * @param {function(email: string): void} onEmailChange
 * @param {function(): void} onEmailFocus
 * @param {function(firstName: string): void} onFirstNameChange
 * @param {function(): void} onFirstNameFocus
 * @param {function(lastName: string): void} onLastNameChange
 * @param {function(): void} onLastNameFocus
 * @param {function(isLegalGuardian: boolean): void} onLegalGuardianChange
 * @param {function(): void} onMonthFocus
 * @param {function(sex: string): void} onSexChange
 * @param {function(): void} onYearFocus
 * @param {boolean} showDateError
 * @param {boolean} showLegalGuardianError
 * @param {boolean} showNameError
 * @param {boolean} showSexError
 * @returns {JSX.Element}
 * @constructor
 */
export class NewSubpatientFormView extends Component {
    state = {};

    handleBlur = (e) => {
        this.props.onEmailChange(e.target.value);
    };

    render() {
        const {
            emailFormatError,
            invalidEmailMessage,
            onBirthDateChange,
            onClickContinue,
            onDayFocus,
            onEmailFocus,
            onFirstNameChange,
            onFirstNameFocus,
            onLastNameChange,
            onLastNameFocus,
            onLegalGuardianChange,
            onMonthFocus,
            onSexChange,
            onYearFocus,
            showDateError,
            showLegalGuardianError,
            showNameError,
            showSexError,
        } = this.props;

        const emailFormatErrorMessage = emailFormatError
            ? 'Please enter a valid email address'
            : undefined;

        const displayEmailError =
            invalidEmailMessage || emailFormatErrorMessage;

        return (
            <Row>
                <FormRow>
                    <Header data-test="patients-name-label">
                        Patient's name
                    </Header>
                    <FirstLastName
                        onFirstNameChange={onFirstNameChange}
                        onFirstNameFocus={onFirstNameFocus}
                        onLastNameChange={onLastNameChange}
                        onLastNameFocus={onLastNameFocus}
                        showNameError={showNameError}
                    />
                </FormRow>
                <FormRow>
                    <Header data-test="patients-email-label">
                        Patient's email
                        <OptionalHeader>{` (optional)`}</OptionalHeader>
                    </Header>
                    <Textbox
                        className="exclude-from-playback"
                        data-test="patients-email"
                        invalidMessage={displayEmailError}
                        onBlur={this.handleBlur}
                        onFocus={onEmailFocus}
                    />
                </FormRow>
                <FormRow>
                    <Header data-test="patients-dob-label">
                        Patient's date of birth
                    </Header>
                    <DateOfBirthPicker
                        onDateChange={onBirthDateChange}
                        onDayFocus={onDayFocus}
                        onMonthFocus={onMonthFocus}
                        onYearFocus={onYearFocus}
                        showInvalid={showDateError}
                    />
                </FormRow>
                <FormRow>
                    <Header data-test="patients-legal-guardian-label">
                        Are you the parent or legal guardian of this patient?
                    </Header>
                    <div
                        className="exclude-from-playback"
                        data-test="radio-guardian-container"
                    >
                        <RadioGroup
                            data-test="legal-guardian-radio-group"
                            hasError={showLegalGuardianError}
                            name="patients-legal-guardian"
                            onChangeCallback={onLegalGuardianChange}
                            orientation="horizontal"
                        >
                            <StyledRadio
                                data-test="radio-guardian-yes"
                                text="Yes"
                                value="yes"
                            />
                            <StyledRadio
                                data-test="radio-guardian-no"
                                text="No"
                                value="no"
                            />
                        </RadioGroup>
                    </div>
                    {showLegalGuardianError && (
                        <div>
                            <ErrorMessage data-test="legal-guardian-error">
                                {NEW_SUBPATIENT_STRINGS.LegalGuardianError}
                            </ErrorMessage>
                        </div>
                    )}
                </FormRow>
                <FormRow>
                    <Header data-test="patients-sex-label">
                        Patient's sex assigned at birth
                    </Header>
                    <div
                        className="exclude-from-playback"
                        data-test="radio-sex-container"
                    >
                        <RadioGroup
                            data-test="sex-radio-group"
                            hasError={showSexError}
                            name="patients-sex"
                            onChangeCallback={onSexChange}
                            orientation="horizontal"
                        >
                            <StyledRadio
                                data-test="radio-male"
                                text="Male"
                                value="male"
                            />
                            <StyledRadio
                                data-test="radio-female"
                                text="Female"
                                value="female"
                            />
                        </RadioGroup>
                    </div>
                    <div>
                        {showSexError && (
                            <ErrorMessage data-test="sex-error">
                                {NEW_SUBPATIENT_STRINGS.SexError}
                            </ErrorMessage>
                        )}
                    </div>
                </FormRow>
                <FormRow>
                    <Button
                        data-test="continue-button"
                        fluid
                        onClick={onClickContinue}
                    >
                        Continue
                    </Button>
                </FormRow>
            </Row>
        );
    }
}

const Header = styled.div`
    font: 15px/20px ${FontFamily.SharpSansSemibold};
    padding-top: 1px;
    padding-bottom: 3px;
`;

const OptionalHeader = styled.span`
    font: 15px/24px ${FontFamily.SharpSansMedium};
    padding-top: 1px;
    padding-bottom: 3px;
`;

const StyledRadio = styled(Radio)`
    width: 50%;
`;
